import { useEffect, useState } from "react";
import FundingChart, { type FundingChartRow } from "./FundingChart";
import { LayoutGrid } from "lucide-react";
import FundingTable from "../components/FundingTable";
import { getSessionStore } from "app/utils/sessionStore";
import { getObjectFromLocalStore } from "app/utils/localStore";
import { makeRequest } from "app/utils/makeRequest";
import { THEME, USERTYPE } from "app/utils/types";
import TrueIqIcon from "app/assets/IconComponent/TrueIqIcon";
import { GoTriangleDown, GoTriangleUp } from "react-icons/go";
import { Accordion, AccordionTab } from "primereact/accordion";
import { AnimatedCheckbox } from "app/uicomponents/microcomponents/AnimatedChecbox";

// ── Types ─────────────────────────────────────────────────────────────────────

interface InsuredPerson {
  id: string;
  name: string;
}

type FundingRow = FundingChartRow;

interface ApiGraphItem {
  planYear: number;
  year: number;
  insuredAge: number;
  clientPremium: { actual: number; illustrated: number; trueIQIndex: number };
  lenderPremium: { actual: number; illustrated: number; trueIQIndex: number };
  interestPayment: { actual: number; illustrated: number; trueIQIndex: number };
  planFee: { actual: number; illustrated: number; trueIQIndex: number };
  originationFee: { illustrated: number };
  combinedTrueIQIndex: number;
  interestPaymentInvoiceStatus: string;
  lenderPremiumInvoiceStatus: string;
  planFeeInvoiceStatus: string;
}

interface ApiTableItem {
  planYear: number; // sequential (1, 2, 3…)
  year: number; // calendar year (2026)
  insuredAge: number;
  clientPremium: { actual: number; illustrated: number };
  lenderPremium: { actual: number; illustrated: number };
  interestPayment: { actual: number; illustrated: number };
  planFee: { actual: number; illustrated: number };
  originationFee: { actual: number; illustrated: number };
  total: { actual: number; illustrated: number };
}

// API planYear = sequential (1,2,3…), API year = calendar year (2026)
// FundingRow.year = sequential, FundingRow.planYear = calendar year
const mapToFundingRow = (item: ApiGraphItem): FundingRow => ({
  planYear: item.year,
  year: item.planYear,
  insuredAge: item.insuredAge,
  clientPremium: item.clientPremium.actual,
  clientPlanFee: item.planFee.actual,
  clientInterestPayment: item.interestPayment.actual,
  clientOriginationFees: item.originationFee?.illustrated ?? 0,
  lenderPremium: item.lenderPremium.actual,
  lenderOriginationFees: 0,
  totalPolicyPremium: item.clientPremium.actual + item.lenderPremium.actual,
  clientPremiumIllustrated: item.clientPremium.illustrated,
  lenderPremiumIllustrated: item.lenderPremium.illustrated,
  clientInterestPaymentIllustrated: item.interestPayment.illustrated,
  clientPlanFeeIllustrated: item.planFee.illustrated,
  combinedTrueIQIndex: item.combinedTrueIQIndex,
  interestPaymentInvoiceStatus: item.interestPaymentInvoiceStatus,
  lenderPremiumInvoiceStatus: item.lenderPremiumInvoiceStatus,
  planFeeInvoiceStatus: item.planFeeInvoiceStatus,
});

const mapTableItemToFundingRow = (item: ApiTableItem): any => ({
  planYear: item.year, // calendar year → "Plan Year" column
  year: item.planYear, // sequential    → "Year" column + row highlight
  insuredAge: item.insuredAge,
  clientPremium: item.clientPremium.actual,
  clientPlanFee: item.planFee.actual,
  clientInterestPayment: item.interestPayment.actual,
  clientOriginationFees: item.originationFee.actual,
  lenderPremium: item.lenderPremium.actual,
  lenderOriginationFees: 0,
  totalPolicyPremium: item.total.actual,
  clientPremiumIllustrated: item.clientPremium.illustrated,
  lenderPremiumIllustrated: item.lenderPremium.illustrated,
  clientInterestPaymentIllustrated: item.interestPayment.illustrated,
  clientPlanFeeIllustrated: item.planFee.illustrated,
});

// ── Filter config ─────────────────────────────────────────────────────────────

const PRIMARY = "#1E3A8A";

interface FilterOption {
  id: string;
  label: string;
  color: string;
  field: keyof Pick<
    FundingRow,
    | "clientPremium"
    | "clientPlanFee"
    | "clientInterestPayment"
    | "clientOriginationFees"
    | "lenderPremium"
  >;
}

const FILTER_OPTIONS: FilterOption[] = [
  { id: "CLIENT_PREMIUM", label: "Client Premium", color: PRIMARY, field: "clientPremium" },
  { id: "LENDER_PREMIUM", label: "Lender Premium", color: `${PRIMARY}cc`, field: "lenderPremium" },
  {
    id: "INTEREST_PAYMENT",
    label: "Interest Payment",
    color: `${PRIMARY}99`,
    field: "clientInterestPayment",
  },
  { id: "PLAN_FEE", label: "Plan Fee", color: `${PRIMARY}66`, field: "clientPlanFee" },
  {
    id: "ORIGINATION_FEE",
    label: "Origination Fee",
    color: `${PRIMARY}33`,
    field: "clientOriginationFees",
  },
];

// ── Sub-components ────────────────────────────────────────────────────────────

const accordionHeader = (title: string) => (
  <div className="text-lg conthrax-font ml-2 text-color-black">{title}</div>
);

const collapseIcon = (
  <div className="rounded-full bg-[#AEB0C6] p-[1px] cursor-pointer">
    <GoTriangleDown
      className="text-[#8A8AA3]/70"
      size={27}
      style={{ transition: "transform 0.2s" }}
    />
  </div>
);
const expandIcon = (
  <div className="rounded-full bg-[#AEB0C6] p-[1px] cursor-pointer">
    <GoTriangleUp
      className="text-[#8A8AA3]/70"
      size={27}
      style={{ transition: "transform 0.2s", rotate: "140deg" }}
    />
  </div>
);
// ── Main component ────────────────────────────────────────────────────────────

function FundingIntRev() {
  const isDark = getSessionStore("globalTheme") === THEME.DARK;
  // const navigate = useNavigate();
  const planId = getSessionStore("planInfo")?.planId;
  const [selectedYear, setSelectedYear] = useState<number>(getSessionStore("ircSelectedYear") ?? 1);
  const [creditType, setCreditType] = useState<string>(
    getSessionStore("ircSelectedCreditType") ?? "preCredit",
  );

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail ?? {};
      const newYear =
        detail.year !== undefined
          ? detail.year
          : getSessionStore("ircSelectedYear") ?? selectedYear;
      const newCreditType =
        detail.creditType !== undefined
          ? detail.creditType
          : getSessionStore("ircSelectedCreditType") ?? creditType;
      console.log("ircSelectionChanged →", { year: newYear, creditType: newCreditType });
      setSelectedYear(newYear);
      setCreditType(newCreditType);
    };
    window.addEventListener("ircSelectionChanged", handler);
    return () => window.removeEventListener("ircSelectionChanged", handler);
  }, []);

  const userAccess = getObjectFromLocalStore("useraccess");
  const isContact = userAccess?.userType === USERTYPE.CONTACT;

  const [insuredPersons, setInsuredPersons] = useState<InsuredPerson[]>([]);

  useEffect(() => {
    if (!planId) return;
    makeRequest("reviewCenterReview")
      .get(planId, true)
      .then((res: any) => {
        const list: any[] = res?.data?.data?.insuredList ?? [];
        setInsuredPersons(
          list.map((item: any) => ({
            id: item.userId,
            name: `${item.firstName ?? ""} ${item.lastName ?? ""}`.trim() || "Insured",
          })),
        );
      })
      .catch(() => {});
  }, [planId]);

  const [selectedInsured, setSelectedInsured] = useState<string>("");
  const [activeFilters, setActiveFilters] = useState<Set<string>>(
    new Set(FILTER_OPTIONS.map((f) => f.id)),
  );
  // const [selectedYear] = useState<number>(1);
  const [fundingData, setFundingData] = useState<FundingRow[]>([]);
  const [tableRows, setTableRows] = useState<FundingRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [showProjections, setShowProjections] = useState(true);
  const [showIndex, setShowIndex] = useState(false);

  // Seed selected insured once persons load
  useEffect(() => {
    if (insuredPersons.length > 0 && !selectedInsured) {
      console.log(insuredPersons, "dasdsdas");
      setSelectedInsured(insuredPersons[0].id);
    }
  }, [insuredPersons, selectedInsured]);

  useEffect(() => {
    // if (!planId || !selectedInsured) return;
    setLoading(true);
    // Always send ALL filter IDs so the backend computes combinedTrueIQIndex
    // across every funding component, regardless of which are toggled in the UI.
    const allFilterIds = JSON.stringify(FILTER_OPTIONS.map((f) => f.id));
    const insuredIds = JSON.stringify([selectedInsured]);
    makeRequest("interactiveReviewCenterFunding")
      .get(
        `${planId}?filters=${allFilterIds}&year=${selectedYear}&creditType=${creditType}&insuredIds=${insuredIds}`,
        true,
      )
      .then((res: any) => {
        const graphData: ApiGraphItem[] = res?.data?.data?.graphData ?? [];
        setFundingData(graphData.map(mapToFundingRow));
        const tableData: ApiTableItem[] = res?.data?.data?.tableData ?? [];
        setTableRows(tableData.map(mapTableItemToFundingRow));
      })
      .catch((err: any) => console.error("Failed to fetch funding data:", err))
      .finally(() => setLoading(false));
  }, [planId, activeFilters, selectedYear, selectedInsured]);

  const insuredName = insuredPersons.find((p) => p.id === selectedInsured)?.name ?? "";

  const cardBg = isDark ? "bg-[#2A2A3D]" : "bg-white";
  const textSub = isDark ? "text-gray-400" : "text-gray-500";
  const toggleFilter = (id: string) => {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col gap-5 pb-8">
      {/* ── Insured persons + TrueIQ ─────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p
            className={`text-[18px] conthrax-font font-semibold uppercase tracking-wider mb-2 ${textSub}`}
          >
            Insured Person(s)
          </p>
          <div className="flex items-center gap-3 flex-wrap">
            {insuredPersons.map((person) => (
              <AnimatedCheckbox
                key={person.id}
                checked={selectedInsured === person.id}
                onChange={() => setSelectedInsured(person.id)}
                inputId={person.id}
                label={person.name}
                brandingColor={PRIMARY}
                size={20}
              />
            ))}
          </div>
        </div>

        {/* TrueIQ badge */}
        <div
          className="flex flex-col rounded-md items-center gap-1.5 px-5 py-2"
          style={{
            border: `1px solid ${PRIMARY}55`,
            color: isDark ? "#93C5FD" : PRIMARY,
            background: isDark ? `${PRIMARY}15` : `${PRIMARY}08`,
          }}
        >
          <div className="flex gap-1 items-center">
            <TrueIqIcon width={18} fill="#55556D" />
            <span className="text-md conthrax-font text-[#55556D]">TrueIQ</span>
          </div>

          <AnimatedCheckbox
            checked={showIndex}
            onChange={() => setShowIndex((v) => !v)}
            inputId="trueiq-index"
            label="Index"
            brandingColor={PRIMARY}
            size={20}
            hideBorder
          />
        </div>
      </div>

      {/* ── Funding filter checkboxes ────────────────────────────────────── */}
      <div>
        <p
          className={`text-[18px] font-semibold uppercase conthrax-font tracking-wider mb-2 ${textSub}`}
        >
          Funding
        </p>
        <div className="flex items-center gap-3 flex-wrap">
          {FILTER_OPTIONS.map((f) => (
            <AnimatedCheckbox
              key={f.id}
              checked={activeFilters.has(f.id)}
              onChange={() => toggleFilter(f.id)}
              inputId={f.id}
              label={
                <span className="flex items-center gap-2">
                  {f.label}

                  <span
                    className="w-[12px] h-[12px] rounded-full inline-block"
                    style={{ backgroundColor: f.color }}
                  />
                </span>
              }
              brandingColor={f.color}
              size={20}
            />
          ))}
        </div>
      </div>

      <Accordion
        collapseIcon={collapseIcon}
        expandIcon={expandIcon}
        multiple
        activeIndex={[0, 1]}
        className="bg-gray-100 dark:!bg-[#111111] rounded-md ip-project-accordion-main"
      >
        <AccordionTab
          headerTemplate={accordionHeader("Funding Chart")}
          headerClassName="interactive-proposal-projection"
          className="bg-light-primary dark:bg-[#2A2A3D] rounded-md"
        >
          <div className={`rounded-2xl shadow-md overflow-hidden ${cardBg}`}>
            {/* <div className={`flex items-center justify-between px-4 py-3 border-b ${divider}`}>
              <div className="flex items-center gap-3">
                <SectionBadge label={selectedYear} isDark={isDark} />
                <span className={`text-[15px] font-semibold ${textMain}`}>Funding Chart</span>
              </div>
            </div> */}

            <div className="px-4 pt-3 pb-2">
              <FundingChart
                data={fundingData}
                activeFilters={activeFilters}
                isDark={isDark}
                selectedYear={selectedYear}
                loading={loading}
                showProjections={showProjections}
                onShowProjectionsChange={setShowProjections}
                isContact={isContact}
                showIndex={showIndex}
              />
            </div>

            <div className="flex items-center px-4 pb-3 gap-2">
              <LayoutGrid size={15} className={`cursor-pointer flex-shrink-0 ${textSub}`} />
              <p className={`text-[11px] text-center flex-1 ${textSub}`}>
                Plan Funding for {insuredName}
              </p>
            </div>
          </div>
        </AccordionTab>

        <AccordionTab
          headerTemplate={accordionHeader("Funding Table")}
          headerClassName="interactive-proposal-projection"
          className="bg-light-primary dark:bg-[#2A2A3D] rounded-md"
        >
          <FundingTable
            data={tableRows}
            selectedYear={selectedYear}
            insuredName={insuredName}
            isDark={isDark}
            cardBg={cardBg}
            // divider={divider}
            // textMain={textMain}
            textSub={textSub}
            activeFilters={activeFilters}
          />
        </AccordionTab>
      </Accordion>
    </div>
  );
}

export default FundingIntRev;
